import { useEffect, useState } from 'react';

export function Hero() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-[#0A0A0A]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/a337782bf2abbca82dddb00376c78d82.jpg"
          alt="Premium window tinting luxury vehicle"
          className="w-full h-full object-cover object-top opacity-70"
        />
        {/* Layered overlays for cinematic depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/55 to-[#0A0A0A]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
      </div>

      {/* Accent glows */}
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-[#FF6B35]/6 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-[#FF6B35]/4 blur-[80px] pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full pt-24 pb-16">
          <div className="max-w-3xl">
            {/* Badge */}
            <div
              className={`anim-hidden ${loaded ? 'animate-fade-in' : ''} inline-flex items-center gap-2 mb-8`}
            >
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FF6B35] text-xs" />
                ))}
              </div>
              <span className="text-white/60 text-xs font-medium tracking-widest uppercase">
                Palm Beach County's #1 Rated Tint Shop
              </span>
            </div>

            {/* H1 */}
            <h1
              className={`anim-hidden ${loaded ? 'animate-fade-in-up delay-100' : ''} text-5xl sm:text-6xl lg:text-[82px] font-bold text-white leading-[1.02] tracking-tight mb-6`}
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              Window Tinting
              <br />
              <span className="shimmer-gold">Palm City, FL</span>
            </h1>

            {/* Subheadline */}
            <p
              className={`anim-hidden ${loaded ? 'animate-fade-in-up delay-200' : ''} text-lg sm:text-xl text-white/70 leading-relaxed mb-10 max-w-xl`}
            >
              Automotive, Residential &amp; Marine Window Tinting — professional
              precision installations backed by a lifetime warranty.
            </p>

            {/* CTAs */}
            <div
              className={`anim-hidden ${loaded ? 'animate-fade-in-up delay-300' : ''} flex flex-col sm:flex-row gap-4 mb-14`}
            >
              <a
                href="#quote"
                className="group relative inline-flex items-center justify-center gap-3 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-8 py-4 rounded-full text-base font-bold tracking-wide transition-all hover:scale-105 whitespace-nowrap cursor-pointer overflow-hidden"
              >
                <span className="relative z-10">Get a Fast Quote</span>
                <i className="ri-arrow-right-line relative z-10 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="tel:+15612759473"
                className="inline-flex items-center justify-center gap-3 border border-white/25 hover:border-white/60 text-white px-8 py-4 rounded-full text-base font-semibold transition-all hover:bg-white/5 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-[#FF6B35]" />
                Call Now — Same-Day Service
              </a>
            </div>

            {/* Stats */}
            <div
              className={`anim-hidden ${loaded ? 'animate-fade-in-up delay-400' : ''} flex flex-wrap items-center gap-8`}
            >
              {[
                { value: '15+', label: 'Years Experience' },
                { value: '10K+', label: 'Vehicles Tinted' },
                { value: '5.0★', label: 'Google Rating' },
                { value: 'Lifetime', label: 'Warranty' },
              ].map((stat) => (
                <div key={stat.value} className="text-left">
                  <div
                    className="text-2xl font-bold text-white tracking-tight"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-xs text-white/50 uppercase tracking-widest mt-0.5">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom lead-form teaser */}
      <div
        className={`anim-hidden ${loaded ? 'animate-fade-in delay-500' : ''} relative z-10 border-t border-white/8`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm">
              🔥 &nbsp;Limited offer: Tint 3 windows, get 1 FREE — expires soon
            </p>
            <a
              href="#quote"
              className="text-[#FF6B35] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
            >
              Claim Offer <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-16 sm:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-white/30" />
        <i className="ri-arrow-down-s-line text-white/30 text-xl" />
      </div>
    </section>
  );
}
